import React, { useMemo, useEffect, useCallback } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Controls,
  ControlButton,
  MiniMap,
  type Node,
  type Edge,
  type Connection,
  useNodesState,
  useEdgesState,
  addEdge as rfAddEdge,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { ArrowRightLeft, ArrowDownUp } from 'lucide-react';
import { verticalNodeTypes, horizontalNodeTypes } from './flow-nodes.js';
import type { StepWarning, FlowDirection } from './flow-nodes.js';
import type { WorkflowEdgeForm } from '../../pages/admin/WorkflowBuilderTypes.js';

export type { FlowDirection };
export type { StepWarning };

// ==========================================
// Types
// ==========================================

export interface DiagramStep {
  id: string;
  order: number;
  name: string;
  description?: string;
  templateSlug?: string | null;
  executionMode: 'sequential' | 'parallel' | 'conditional' | 'race' | null;
  parallelGroup?: string | null;
  optional?: boolean;
  diagramPosition?: { x: number; y: number };
  label?: string;
  debugStatus?: 'none' | 'has_output' | 'drifted';
  warnings?: StepWarning[];
  hasContract?: boolean;
  approved?: boolean;
  outputParts?: string[];
  /** Available for edge contract-health analysis */
  outputContractFields?: string[];
  inputContractFields?: string[];
}

interface Props {
  steps: DiagramStep[];
  selectedStepId?: string | null;
  onStepClick?: (stepId: string) => void;
  onOutputClick?: (stepId: string) => void;
  direction?: FlowDirection;
  onDirectionToggle?: () => void;
  onPositionsChange?: (positions: Record<string, { x: number; y: number }>) => void;
  // ── Canvas editing ──
  editable?: boolean;
  /** User-drawn dependency edges from form state */
  formEdges?: WorkflowEdgeForm[];
  /** Called when user connects two handles */
  onAddEdge?: (sourceStepId: string, targetStepId: string) => void;
  /** Called when user deletes a step node (Delete/Backspace key) */
  onRemoveStep?: (stepId: string) => void;
  /** Called when user deletes an edge */
  onRemoveEdge?: (sourceStepId: string, targetStepId: string) => void;
  /** Called when user drops a palette item onto the canvas */
  onDropStep?: (kind: string, position: { x: number; y: number }) => void;
  /** Called when user commits an inline rename */
  onUpdateStep?: (stepId: string, updates: { name: string }) => void;
}

// ==========================================
// Edge health color (ReactFlow stroke — hex is allowed for RF internals per design-doc)
// ==========================================

function edgeHealthStroke(health: 'compatible' | 'mismatch' | 'unknown' | undefined): string {
  if (health === 'compatible') return 'var(--color-success)';
  if (health === 'mismatch') return 'var(--color-warning)';
  return 'var(--color-faint)';
}

function analyzeEdgeHealth(
  sourceFields: string[] | undefined,
  targetFields: string[] | undefined,
): 'compatible' | 'mismatch' | 'unknown' {
  if (!sourceFields?.length || !targetFields?.length) return 'unknown';
  const missing = targetFields.filter((f) => !sourceFields.includes(f));
  return missing.length === 0 ? 'compatible' : 'mismatch';
}

// ==========================================
// Layout builder
// ==========================================

function buildLayout(
  steps: DiagramStep[],
  selectedStepId: string | null | undefined,
  onStepClick: ((stepId: string) => void) | undefined,
  onOutputClick: ((stepId: string) => void) | undefined,
  onUpdateStep: ((stepId: string, updates: { name: string }) => void) | undefined,
  direction: FlowDirection,
  editable: boolean,
  formEdges: WorkflowEdgeForm[],
): { nodes: Node[]; baseEdges: Edge[] } {
  if (steps.length === 0) return { nodes: [], baseEdges: [] };

  const nodes: Node[] = [];
  const autoEdges: Edge[] = [];

  const byTier = new Map<number, DiagramStep[]>();
  steps.forEach((s) => {
    const tier = s.order ?? 0;
    if (!byTier.has(tier)) byTier.set(tier, []);
    byTier.get(tier)!.push(s);
  });

  const sortedTiers = Array.from(byTier.keys()).sort((a, b) => a - b);
  const isHorizontal = direction === 'horizontal';
  const primarySpacing = isHorizontal ? 300 : 170;
  const secondarySpacing = isHorizontal ? 140 : 260;

  sortedTiers.forEach((tier, tierIdx) => {
    const tierSteps = byTier.get(tier)!;
    const totalSecondary = (tierSteps.length - 1) * secondarySpacing;
    const startSecondary = -totalSecondary / 2;

    tierSteps.forEach((step, idx) => {
      const primary = tierIdx * primarySpacing;
      const secondary = startSecondary + idx * secondarySpacing;
      const autoPosition = isHorizontal ? { x: primary, y: secondary } : { x: secondary, y: primary };
      const position = step.diagramPosition ?? autoPosition;

      nodes.push({
        id: step.id,
        type: 'step',
        position,
        data: {
          label: step.name,
          stepId: step.id,
          order: step.label || String(step.order),
          templateSlug: step.templateSlug,
          executionMode: step.executionMode,
          optional: step.optional,
          debugStatus: step.debugStatus,
          warnings: step.warnings,
          hasContract: step.hasContract,
          approved: step.approved,
          selected: step.id === selectedStepId,
          editable,
          onStepClick,
          onUpdateStep,
        },
      });

      if (step.outputParts && step.outputParts.length > 0) {
        const outputId = `${step.id}-output`;
        const outputPos = isHorizontal
          ? { x: position.x, y: position.y + 90 }
          : { x: position.x + 200, y: position.y + 20 };
        nodes.push({
          id: outputId,
          type: 'stepOutput',
          position: outputPos,
          draggable: true,
          data: { parts: step.outputParts, parentStepId: step.id, onOutputClick },
        });
        autoEdges.push({
          id: `${step.id}-to-output`,
          source: step.id,
          target: outputId,
          type: 'straight',
          style: { stroke: 'var(--color-faint)', strokeWidth: 1, strokeDasharray: '4 3' },
        });
      }
    });

    // Auto tier-to-tier edges only when no user-drawn edges exist
    if (formEdges.length === 0 && tierIdx < sortedTiers.length - 1) {
      const nextTier = sortedTiers[tierIdx + 1];
      const nextSteps = byTier.get(nextTier)!;
      const edgeStyle = { stroke: 'var(--color-faint)', strokeWidth: 2 };

      if (tierSteps.length === 1) {
        for (const ns of nextSteps) {
          autoEdges.push({
            id: `${tierSteps[0].id}-${ns.id}`,
            source: tierSteps[0].id,
            target: ns.id,
            style: edgeStyle,
          });
        }
      } else if (nextSteps.length === 1) {
        for (const cs of tierSteps) {
          autoEdges.push({
            id: `${cs.id}-${nextSteps[0].id}`,
            source: cs.id,
            target: nextSteps[0].id,
            style: edgeStyle,
          });
        }
      } else {
        const cC = tierSteps[Math.floor(tierSteps.length / 2)];
        const cN = nextSteps[Math.floor(nextSteps.length / 2)];
        autoEdges.push({ id: `tier-${tier}-to-${nextTier}`, source: cC.id, target: cN.id, style: edgeStyle });
      }
    }
  });

  return { nodes, baseEdges: autoEdges };
}

// ==========================================
// Project formEdges → ReactFlow edges with health coloring
// ==========================================

function projectFormEdges(formEdges: WorkflowEdgeForm[], steps: DiagramStep[]): Edge[] {
  const stepMap = new Map(steps.map((s) => [String(s.id), s]));
  return formEdges.map((fe) => {
    const src = stepMap.get(String(fe.sourceStepId));
    const tgt = stepMap.get(String(fe.targetStepId));
    const health = analyzeEdgeHealth(src?.outputContractFields, tgt?.inputContractFields);
    const stroke = edgeHealthStroke(health);
    return {
      id: `form-edge-${fe.sourceStepId}-${fe.targetStepId}`,
      source: String(fe.sourceStepId),
      target: String(fe.targetStepId),
      style: { stroke, strokeWidth: 2 },
      label: fe.label?.text,
      data: { sourceStepId: fe.sourceStepId, targetStepId: fe.targetStepId, kind: fe.kind, health },
    };
  });
}

// ==========================================
// Main Component
// ==========================================

export const WorkflowFlowDiagram: React.FC<Props> = (props) => (
  <ReactFlowProvider>
    <WorkflowFlowDiagramInner {...props} />
  </ReactFlowProvider>
);

const WorkflowFlowDiagramInner: React.FC<Props> = ({
  steps,
  selectedStepId,
  onStepClick,
  onOutputClick,
  direction = 'vertical',
  onDirectionToggle,
  onPositionsChange,
  editable = false,
  formEdges = [],
  onAddEdge,
  onRemoveStep,
  onRemoveEdge,
  onDropStep,
  onUpdateStep,
}) => {
  const { screenToFlowPosition } = useReactFlow();

  const layout = useMemo(
    () => buildLayout(steps, selectedStepId, onStepClick, onOutputClick, onUpdateStep, direction, editable, formEdges),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [steps, direction, editable, formEdges.length],
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(layout.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(layout.baseEdges);

  // Re-layout when steps change
  useEffect(() => {
    setNodes((prev) => {
      const draggedOutputPositions = new Map<string, { x: number; y: number }>();
      prev.forEach((n) => {
        if (n.type === 'stepOutput') draggedOutputPositions.set(n.id, n.position);
      });
      return layout.nodes.map((n) => {
        if (n.type === 'stepOutput' && draggedOutputPositions.has(n.id)) {
          return { ...n, position: draggedOutputPositions.get(n.id)! };
        }
        return n;
      });
    });
  }, [layout.nodes, setNodes]);

  // Sync formEdges → local edges (user-drawn edges take precedence over auto-tier edges)
  useEffect(() => {
    if (formEdges.length > 0) {
      const outputEdges = layout.baseEdges.filter((e) => e.id.endsWith('-to-output'));
      setEdges([...outputEdges, ...projectFormEdges(formEdges, steps)]);
    } else {
      setEdges(layout.baseEdges);
    }
  }, [formEdges, layout.baseEdges, steps, setEdges]);

  // Update callbacks without resetting positions
  useEffect(() => {
    setNodes((nds) =>
      nds.map((n) => ({
        ...n,
        data: {
          ...n.data,
          selected: n.id === selectedStepId,
          onStepClick,
          onUpdateStep,
          editable,
          ...(n.type === 'stepOutput' ? { onOutputClick } : {}),
        },
      })),
    );
  }, [selectedStepId, onStepClick, onOutputClick, onUpdateStep, editable, setNodes]);

  const handleConnect = useCallback(
    (connection: Connection) => {
      if (!connection.source || !connection.target) return;
      onAddEdge?.(connection.source, connection.target);
      // Optimistic local update so the edge appears immediately
      setEdges((es) =>
        rfAddEdge(
          {
            id: `form-edge-${connection.source}-${connection.target}`,
            source: connection.source!,
            target: connection.target!,
            style: { stroke: 'var(--color-faint)', strokeWidth: 2 },
            data: { sourceStepId: Number(connection.source), targetStepId: Number(connection.target), kind: 'data' },
          },
          es,
        ),
      );
    },
    [onAddEdge, setEdges],
  );

  const handleNodesDelete = useCallback(
    (deletedNodes: Node[]) => {
      for (const node of deletedNodes) {
        if (node.type === 'step') onRemoveStep?.(node.id);
      }
    },
    [onRemoveStep],
  );

  const handleEdgesDelete = useCallback(
    (deletedEdges: Edge[]) => {
      for (const edge of deletedEdges) {
        const src = edge.data?.sourceStepId;
        const tgt = edge.data?.targetStepId;
        if (src !== undefined && tgt !== undefined) {
          onRemoveEdge?.(String(src), String(tgt));
        }
      }
    },
    [onRemoveEdge],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const kind = e.dataTransfer.getData('application/node-type');
      if (!kind || !onDropStep) return;
      const position = screenToFlowPosition({ x: e.clientX, y: e.clientY });
      onDropStep(kind, position);
    },
    [onDropStep, screenToFlowPosition],
  );

  if (steps.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-muted text-sm">
        {editable ? 'Drag a node from the palette or click + Add Step' : 'Add steps to see the execution flow'}
      </div>
    );
  }

  return (
    <div
      className="h-full min-h-[200px] bg-bg rounded-sm border relative"
      onDragOver={editable ? handleDragOver : undefined}
      onDrop={editable ? handleDrop : undefined}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={editable ? handleConnect : undefined}
        onNodesDelete={editable ? handleNodesDelete : undefined}
        onEdgesDelete={editable ? handleEdgesDelete : undefined}
        onNodeDragStop={(_event, _node, allNodes) => {
          if (onPositionsChange) {
            const positions: Record<string, { x: number; y: number }> = {};
            allNodes.forEach((n) => {
              if (!n.id.endsWith('-output')) positions[n.id] = n.position;
            });
            onPositionsChange(positions);
          }
        }}
        nodeTypes={direction === 'horizontal' ? horizontalNodeTypes : verticalNodeTypes}
        deleteKeyCode={editable ? ['Delete', 'Backspace'] : null}
        selectionKeyCode={editable ? 'Shift' : null}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.3}
        maxZoom={1.5}
        proOptions={{ hideAttribution: true }}
      >
        <Controls showInteractive={false}>
          {onDirectionToggle && (
            <ControlButton
              onClick={onDirectionToggle}
              title={direction === 'vertical' ? 'Horizontal layout' : 'Vertical layout'}
            >
              {direction === 'vertical' ? <ArrowRightLeft size={12} /> : <ArrowDownUp size={12} />}
            </ControlButton>
          )}
        </Controls>
        <MiniMap
          nodeColor={(node) => {
            if (node.type === 'stepOutput') return 'var(--color-heading)';
            if (node.data?.selected) return 'var(--color-accent)';
            const warnings = (node.data?.warnings ?? []) as StepWarning[];
            if (warnings.some((w) => w.severity === 'error')) return 'var(--color-danger)';
            if (warnings.some((w) => w.severity === 'warning')) return 'var(--color-warning)';
            const mode = node.data?.executionMode;
            if (mode === 'parallel') return 'var(--color-purple)';
            if (mode === 'conditional') return 'var(--color-warning)';
            return 'var(--color-faint)';
          }}
          style={{ background: 'var(--color-surface-alt)' }}
        />
      </ReactFlow>
    </div>
  );
};
